'use client';

import './expandedContent-tabbed.scss';

import { useState } from 'react';

import { AccordionGroup } from '@sola/ark-core/molecules/accordion-group/accordion-group.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';

import { Image } from '../../../atoms/image/image.tsx';
import { FeatureBase } from '../../../molecules/feature-base/feature-base.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import { getPlaceholderItems } from '../../../utils/storybook.tsx';

export const ExpandedContentTabbed: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  items,
  ...props
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0);

  const handleItemClick = (index: number) => {
    setCurrentStep(index);

    const open = document.querySelector(`[data-state="open"]`);
    setTimeout(() => {
      if (open && window.innerWidth < 768) {
        open.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const hasHeaderContent = !!props.title || !!props.body;
  const tabbedItems =
    !items || items.length < 1 ? getPlaceholderItems(items, 1) : items;

  return (
    <section className={cn('ExpandedContentTabbed component')}>
      <div className="ExpandedContentTabbed__container container container--inline-box flex flex-col gap-roomy-xs lg:gap-roomy-sm">
        {hasHeaderContent && <HeaderGroup {...props} seoLevel={seoLevel} />}

        <div
          className="ExpandedContentTabbed__items flex flex-col-reverse md:flex-row gap-comfy-sm md:gap-comfy-xl"
          data-length={tabbedItems.length}
        >
          <div className="ExpandedContentTabbed__content">
            <div className="ExpandedContentTabbed__accordion">
              <AccordionGroup
                onChange={handleItemClick}
                variant="tabbed"
                items={tabbedItems.map(
                  ({
                    title,
                    subtitle: _subtitle,
                    media: _media,
                    icon: _icon,
                    badge,
                    ...item
                  }) => ({
                    header: title || '',
                    badge: badge,
                    seoLevel: hasHeaderContent ? seoLevel + 1 : seoLevel,
                    children: (
                      <FeatureBase
                        {...item}
                        variant="standard"
                        bodySize="sm"
                        buttonSize="sm"
                        inline
                      />
                    ),
                  }),
                )}
              />
            </div>
          </div>
          <div className="ExpandedContentTabbed__image">
            <Image
              image={tabbedItems[currentStep].media?.image}
              rounded="md"
              aspectRatio="square"
              hasPlaceholder
            />
          </div>
        </div>
      </div>
    </section>
  );
};
